import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Divider,
  Chip,
  Card,
  CardContent,
} from '@mui/material';
import {
  Info as InfoIcon,
  CalendarToday as CalendarIcon,
  Tag as TagIcon,
} from '@mui/icons-material';
import versionData from '../version.json';

interface ChangelogRelease {
  version: string;
  date: string;
  added: string[];
  modified: string[];
  fixed: string[];
}

const AboutPage: React.FC = () => {
  const [changelog, setChangelog] = useState<ChangelogRelease[]>([]);

  useEffect(() => {
    // Parse del changelog
    // In produzione, potresti voler leggere il file CHANGELOG.md via API
    // Per ora, lo manteniamo hardcoded con i dati principali
    const changelogData: ChangelogRelease[] = [
      {
        version: '1.6',
        date: '2026-03-29',
        added: [
          'Dashboard Investimenti (/analytics/investimenti): nuova pagina per monitorare i progetti ad investimento. Filtri: anno (dal 2020), struttura, risorse (multi-select), gruppo progetti (preseleziona il gruppo id=40 se esiste), progetto (multi-select con ricerca testuale, mostra id_hrp tra parentesi). 6 KPI con confronto anno precedente e delta. Grafico andamento mensile gg/uu. Tabelle per progetto e per risorsa con colonne Gg Rend. | € Rend. | Gg Pian. | € Pian. | € Tot. e riga Totali. Grafico a torta ripartizione Investimento vs Produttivo.',
          'Tabella prev_pianificazioni: salvataggio automatico delle pianificazioni manuali (sorgente=M) prima della sovrascrittura da sync rendicontazioni HRP. La prima pianificazione viene preservata (ON CONFLICT DO NOTHING). Predisposta per futura dashboard scostamenti pianificato vs rendicontato.',
          'Variabile HH_FTE in .env: ore lavorative mensili per 1 FTE, configurabile (default 176 = 22 gg × 8h). Tutti i calcoli (rendicontazioni, economics, investimenti, capacity planning, budget, consolidamento) ora leggono da questa variabile. Risolta inconsistenza preesistente tra 20 gg/mese e 22 gg/mese.',
        ],
        modified: [
          'Rebranding: MyPlanner rinominato in HarpaPlanner in tutte le etichette visibili (login, sidebar, tab browser, about, sync). Nomi interni (database, variabili, file, path) invariati.',
          'Logo HarpaPlanner: nuovo logo inserito in favicon browser, pagina login, sidebar e pagina about.',
        ],
        fixed: [
          'Rimossa tutta la funzionalità AI Chat (ChatPage, ChatbotUI, backend AI services, provider, prompts). Predisposto per futura integrazione Synapta AI.',
        ],
      },
      {
        version: '1.5.10.9',
        date: '2026-03-19',
        added: [
          'Vista Piano — celle mensili colorate: ogni cella della tabella mostra la percentuale di allocazione con colore verde (≥95%), giallo (≥70%) o rosso (<70%), sia sulla riga risorsa (totale mese) che sulle righe progetto. Celle vuote rimangono senza colore.',
          'Dashboard Dirigenziale — KPI "Progetti in uso NNN/TTT": NNN = progetti con almeno una allocazione/rendicontazione nell\'anno e filtri selezionati; TTT = totale progetti attivi (enabled). Sostituisce il precedente contatore statico "Progetti Attivi".',
        ],
        modified: [],
        fixed: []
      },
      {
        version: '1.5.10.8',
        date: '2026-03-19',
        added: [
          'Tabella clienti: nuova tabella MyPlanner che replica l\'anagrafica clienti HRP (id_hrp, nome).',
          'Sync Clienti (Admin → Sync Clienti, /admin/sync-clienti): nuova pagina per sincronizzare tutti i clienti da HRP in un\'unica operazione. Da eseguire prima della Sync Progetti.',
        ],
        modified: [
          'Sync Progetti: rimosso il JOIN con d_cliente in HRP; il nome cliente viene ora risolto dalla tabella locale clienti tramite loadClientiMap() prima del loop di sync.',
          'Tutte le API che restituiscono il campo cliente (progetti, pm-dashboard, economics, dashboard, export, gruppi-progetti) ora eseguono LEFT JOIN clienti e restituiscono il nome al posto dell\'ID numerico tramite COALESCE(cl.nome, p.cliente).',
        ],
        fixed: [
          'PM Dashboard (/pm-dashboard): errore SQL "column cl.nome must appear in GROUP BY" — aggiunto cl.nome al GROUP BY della query progetti.',
          'Anagrafiche Progetti — ricerca per id_hrp non funzionante dopo introduzione JOIN clienti: la condizione WHERE id_hrp era ambigua (presente sia in progetti che in clienti). Qualificata come p.id_hrp.',
          'Dashboard Dirigenziale — aggiunto filtro Struttura nella toolbar: filtra tutte le widget per struttura selezionata agendo su selectedRisorse.',
        ]
      },
      {
        version: '1.5.10.7',
        date: '2026-03-19',
        added: [
          'Nuova pagina "Vista Piano" (Pianificazione → Vista Piano, /pianificazione/vista): tabella pivot risorse/mesi con accordion drill-down per progetto. Riusa API export esistente senza nuovi endpoint backend.',
          'Vista Piano — colonna "% All": media mensile delle allocazioni per riga (risorsa o progetto), con badge colorato (verde ≥95%, giallo ≥70%, rosso <70%) per identificare a colpo d\'occhio le risorse sotto-allocate.',
          'Vista Piano — accordion drill-down: cliccando sulla riga risorsa si espande il dettaglio con una riga per ogni progetto allocato (ID HRP, Titolo, PM, Cliente, Budget, Residuo, % All, allocazioni mensili).',
          'Vista Piano — toolbar: filtro Anno (dal 2020, default anno corrente), filtro Struttura (tutte di default), pulsanti "Espandi tutto" / "Collassa tutto".',
          'Vista Piano — ordinamento automatico per Struttura poi Cognome.',
          'Vista Piano — pulsante "Esporta Excel" integrato in toolbar: esporta i dati visibili rispettando i filtri Anno e Struttura attivi. Stessa formattazione della vecchia pagina export (header blu, righe alternate, freeze, autofilter). Nome file include la struttura selezionata se filtrata.',
        ],
        modified: [
          'Pagina "Esporta" rimossa dal menu: la funzionalità è ora integrata in "Vista Piano".',
          'Sync Progetti (syncProgetti.js): il campo cliente ora salva il nome del cliente (JOIN su d_cliente in HRP) invece dell\'ID numerico. La prossima sincronizzazione aggiornerà i dati esistenti.',
        ],
        fixed: [
          'Clone pianificazione (MonthGrid): dopo la copia mese, mancava la chiamata refreshProgetti() — i totali budget/residuo non si aggiornano nel frontend. Fix: aggiunta chiamata refreshProgetti() al termine del loop di copia.',
        ]
      },
      {
        version: '1.5.10.6',
        date: '2026-03-18',
        added: [],
        modified: [],
        fixed: [
          'Sync Rendicontazioni — logica DELETE corretta: cancella tutte le allocazioni (H e M) dal mese di lastSync fino al mese corrente incluso (CURDATE). I mesi futuri (prossimo mese in poi) non vengono mai toccati.',
          'Sync Rendicontazioni — query HRP corretta: usa data_inizio >= primo giorno del mese di lastSync (non > lastSyncTimestamp esatto), allineando perfettamente il range della query con quello del DELETE. Elimina il bug che causava il mese corrente vuoto dopo sync ripetuti.',
          'Sync Rendicontazioni — newSyncTimestamp salvato come primo giorno del mese corrente: garantisce che il prossimo sync ri-processi sempre il mese in corso (dati ancora in arrivo da HRP).',
        ]
      },
      {
        version: '1.5.10.5',
        date: '2026-03-16',
        added: [
          'Sync Rendicontazioni (/admin/sync-rendicontazioni): selettore risorse — possibile scegliere una o più risorse prima di avviare preview/sync. Default: tutte le risorse selezionate. Chip riepilogativo (verde = tutte, arancio = parziale), pulsanti "Seleziona tutte" / "Deseleziona tutte", Autocomplete multi-select con ricerca e checkbox.',
          'Sync Rendicontazioni: il DELETE in sync-all è ora limitato alle sole risorse selezionate (nessuna cancellazione di dati non coinvolti)',
        ],
        modified: [],
        fixed: [
          'Sync Progetti (syncProgetti.js): la procedura di sync impostava sempre enabled=true su tutti i progetti importati, rendendo il filtro "Solo Abilitati" in /pianificazione non funzionante. Ora enabled è derivato da stato_chiuso_id HRP: NULL = progetto aperto (enabled=true), qualsiasi valore = progetto chiuso (enabled=false).',
        ]
      },
      {
        version: '1.5.10.4',
        date: '2026-03-09',
        added: [
          'Totale allocato / rendicontato per progetto: nuove metriche calcolate on-the-fly su tutte le allocazioni (totale_allocato = tutte le sorgenti; totale_rendicontato = solo sorgente HRP)',
          'ProgettiPage (/anagrafiche/progetti): colonne "Tot. Allocato (gg)" e "Tot. Rendicontato (gg)" nella tabella progetti',
          'PMDashboardPage: ogni card progetto mostra i totali complessivi allocato/rendicontato (lifetime, indipendente dal filtro periodo)',
          'Utility centralizzata backend/src/utils/allocazioniTotals.js: unica funzione SQL condivisa da tutti i route — nessuna logica duplicata',
          'Pianificazione (/pianificazione) — selettore GRUPPI: terza opzione di visibilità progetti accanto a "Solo abilitati" e "Tutti". In modalità GRUPPI si visualizza l\'elenco dei gruppi progetto; cliccando un gruppo si entra nella vista filtrata con solo i progetti di quel gruppo (tutti trascinabili sul calendario). Freccia indietro in testa alla lista per tornare all\'elenco gruppi.',
          'GruppoProgettiListItem: bottone ChevronRight fuori dall\'area drag — la selezione del gruppo è ora immediata e non entra in conflitto con il drag-and-drop',
        ],
        modified: [
          'ExportPage: foglio Excel formattato con intestazione colorata (blu), righe alternate, bordi, riga congelata e filtri automatici — libreria xlsx sostituita con exceljs',
          'ExportPage: aggiunta colonna "Budget (gg/uu)" tra Cliente e Budget Residuo',
          'ExportPage: aggiunta colonna "Struttura" come prima colonna nel file Excel esportato',
          'ExportPage: aggiunta colonna "ID HRP" (id progetto HRP) dopo la colonna Nome',
          'PMProgettoDetailPage: aggiornamento dati su blur/Enter invece che ad ogni keystroke nei campi data periodo',
        ],
        fixed: []
      },
      {
        version: '1.5.10.3',
        date: '2026-03-03',
        added: [],
        modified: [
          'PMDashboardPage + PMProgettoDetailPage: filtro anno sostituito con intervallo di date (Da / A), preimpostato sull\'anno corrente con auto-reload al cambio',
          'GruppoProgettiDialog: ricerca progetti server-side con debounce (compatibile con cataloghi da 15.000+ progetti)',
        ],
        fixed: [
          'GruppoProgettiDialog: i progetti non apparivano se superavano il limite di 1000 record — ora la ricerca è server-side',
          'gruppiProgetti.js: colonna p.hrp_id inesistente rinominata in p.id_hrp nelle query GET /:id e PUT /:id',
        ]
      },
      {
        version: '1.5.10.2',
        date: '2026-03-02',
        added: [],
        modified: [
          'PMProgettoDetailPage: ridisegnati i 6 widget della pagina dettaglio progetto PM',
          'Widget 1 — Budget progetto: invariato (budget_gg_uu totale)',
          'Widget 2 — Rendicontato <anno>: spostato in seconda posizione, mostra solo dati HRP reali per l\'anno',
          'Widget 3 — Residuo <anno>: nuovo, calcolato come budget totale − rendicontato anno (rosso se negativo, arancio se <10%)',
          'Widget 4 — Costi a finire: nuovo, somma gg/uu pianificati da domani in poi (mesi futuri rispetto alla data corrente)',
          'Widget 5 — Avanzamento temporale: invariato',
          'Widget 6 — Budget — Anno <anno>: sostituito con barra visuale [rendicontato | residuo] su base budget totale, con legenda valori e percentuali',
        ],
        fixed: [
          'Rimossi widget "Pianificato <anno>" e "Team <anno>" dalla riga KPI del dettaglio progetto PM'
        ]
      },
      {
        version: '1.5.10.1',
        date: '2026-03-02',
        added: [
          'PM Dashboard: nuova sezione "PM Dashboard" nel menu laterale con pagina "I miei progetti"',
          'PMDashboardPage: lista progetti del PM con KPI annuali (pianificato, rendicontato), stato RAG, barra timeline e barra budget per anno selezionato',
          'PMProgettoDetailPage: pagina dettaglio progetto con team allocation grid mensile, grafico a barre carico lavoro, KPI pianificato/rendicontato filtrati per anno',
          'Backend pmDashboard.js: due endpoint REST (/pm-dashboard/progetti e /pm-dashboard/progetti/:id) con calcoli year-specific tramite FILTER (WHERE a.anno = $2)',
          'Identificativo PM configurabile: input pre-compilato con username utente, persistito in localStorage',
          'Filtro anno: tutti i KPI (pianificato, rendicontato, risorse, grafico) filtrati per anno selezionato'
        ],
        modified: [],
        fixed: []
      },
      {
        version: '1.5.10.0',
        date: '2026-03-02',
        added: [],
        modified: [
          'Riorganizzazione backend: file di test, script e documentazione spostati in backend/src/tools/ (sottocartelle scripts/ e docs/)',
          'Configurazione HRP DB da variabili d\'ambiente: rimossi parametri hardcoded in rendicontazioni.js e syncProgetti.js, ora letti da .env (HRP_DB_HOST, HRP_DB_PORT, HRP_DB_USER, HRP_DB_PASSWORD, HRP_DB_DATABASE)',
          '.env.example: allineato con le chiavi HRP_DB_* effettivamente usate dal codice'
        ],
        fixed: [
          'Fix glitch grafico MonthGrid: nomi progetto lunghi ora vanno a capo nel box invece di sforare fuori (rimosso noWrap, aggiunto wordBreak: break-word)'
        ]
      },
      {
        version: '1.5.9.4',
        date: '2025-12-08',
        added: [
          'Code Copy Button: pulsante copia automatico per blocchi di codice nelle risposte ChatbotUI',
          'Message Reactions: sistema feedback thumbs up/down per valutare risposte AI',
          'Prompt Templates: 6 quick actions predefinite MyPlanner-specific (risorse, budget, SQL, capacity planning)',
          'Tool Calls Visualization: visualizzazione trasparente chiamate SQL eseguite dall\'AI con JSON input/output',
          'Markdown Extended: supporto completo tabelle con remarkGfm plugin'
        ],
        modified: [
          'ChatbotUI: aggiunta icona Bolt per toggle panel templates',
          'TypeScript: risolti errori compilazione ReactMarkdown components',
          'SSE Streaming: cattura eventi tool_call dal backend in real-time',
          'State Management: gestione reazioni, template visibility, tool calls in stato React'
        ],
        fixed: [
          'Fix TypeScript TS2339: Property inline non riconosciuta in ReactMarkdown code component',
          'Fix TypeScript TS6133: Parametro language non utilizzato in CodeBlock',
          'Rimossi log di debug dalla produzione'
        ]
      },
      {
        version: '1.5.9.3',
        date: '2025-12-02',
        added: [
          'Capacity Planning 2: nuova dashboard avanzata per analisi domanda vs offerta',
          'Dashboard principale: confronto mensile demand teorico (budget distribuito) vs supply reale (allocazioni)',
          'Drill-down mensile: click su mese per dettaglio progetti e allocazioni risorse',
          'Filtro strutture: multi-select per analisi per strutture organizzative',
          'Calcolo FTE mancanti: persone full-time necessarie per colmare gap negativi',
          'Mese uomo standardizzato a 20 giorni lavorativi (era 22)',
          'Edit inline campi progetti: Budget (gg), Data Inizio, Data Fine editabili direttamente nella tabella',
          'Salvataggio automatico su blur per data entry rapido',
          'Query SQL ottimizzate con CTE per calcolo demand linearizzato',
          'Supporto filtro progetti attivi: solo con budget_gg_uu > 0, date valide, enabled=true'
        ],
        modified: [
          'routes/capacityPlanningV2.js: correzione nomi colonne allocazioni (id_risorsa, id_progetto)',
          'ProgettiPage.tsx: aggiunta funzionalità edit inline con TextField autoFocus',
          'api.ts: aggiunte interfacce Struttura, CapacityPlanningV2Response, CapacityPlanningV2DettaglioResponse',
          'Supply calculation: filtro per progetti attivi per coerenza con demand'
        ],
        fixed: [
          'Fix calcolo supply: ora considera solo allocazioni di progetti con budget attivi',
          'Fix discrepanza numeri tra dashboard e drill-down (era inclusa tutta la supply, ora solo progetti attivi)',
          'Fix query allocazioni: corretti join con id_risorsa e id_progetto (non risorsa_id, progetto_id)'
        ]
      },
      {
        version: '1.5.8.2',
        date: '2025-12-01',
        added: [],
        modified: [],
        fixed: [
          'Fix Connessione Database Gruppi Progetti in Produzione K8s',
          'routes/gruppiProgetti.js: corrette variabili d\'ambiente da PG* a DB_* (standard progetto)',
          'Allineamento configurazione Pool PostgreSQL con altre route (chat.js, progetti.js, risorse.js)',
          'Era: PGUSER, PGHOST, PGDATABASE → Ora: DB_USER, DB_HOST, DB_NAME',
          'Risolto ECONNREFUSED su endpoint /api/gruppi-progetti in produzione'
        ]
      },
      {
        version: '1.5.8.1',
        date: '2025-12-01',
        added: [],
        modified: [],
        fixed: [
          'Fix URL Produzione Gruppi Progetti: risolto errore ERR_CONNECTION_REFUSED in produzione K8s',
          'Refactoring completo per usare sistema API centralizzato con placeholder BASE_URL',
          'config.ts: aggiunto supporto placeholder process.env.REACT_APP_API_URL',
          'services/api.ts: aggiunte 7 funzioni centralizzate per gruppi-progetti',
          'GruppiProgettiPage.tsx: rimosso axios diretto e API_BASE_URL hardcoded',
          'GruppoProgettiDialog.tsx: rimosso apiClient custom, usa funzioni centralizzate',
          'Creazione tabelle gruppi_progetti su database PostgreSQL locale',
          'DDL generato per ambiente produzione (DDL_GRUPPI_PROGETTI_PRODUZIONE.sql)'
        ]
      },
      {
        version: '1.5.8',
        date: '2025-11-25',
        added: [
          'Gestione Gruppi Progetti: creazione, modifica ed eliminazione gruppi di progetti',
          'Drag & Drop Gruppi Progetti: possibilità di trascinare un intero gruppo su un mese per allocazione batch',
          'Skip automatico progetti già allocati durante drop gruppo',
          'Icona gialla distintiva per gruppi progetti (vs rossa progetti singoli)',
          'Admin Page Gruppi Progetti: interfaccia dedicata con card visualizzazione gruppi',
          'Colore personalizzabile per ogni gruppo progetti',
          'Visualizzazione 12 mesi per anno: rimossa limitazione "solo mesi rimanenti"',
          'Mesi consolidati read-only: mesi passati visualizzati ma non modificabili',
          'Chip "Consolidato" sui mesi passati per indicare dati da rendicontazioni',
          'Dialog Copia Pianificazione migliorato: selezione risorsa sorgente + mese sorgente',
          'Default risorsa stessa e mese precedente nel dialog copia',
          'Ricerca progetti per ID HRP: query numerica cerca per id_hrp esatto invece di ILIKE',
          'Display ID HRP nei dettagli progetto (non più nel titolo)'
        ],
        modified: [
          'Rimosso vincolo 100% allocazione risorse: permessa over-allocation',
          'Backend allocazioni: eliminati controlli percentuale > 100% e somma > 100%',
          'Frontend MonthGrid: eliminati controlli lato client per limite 100%',
          'Funzione copia pianificazione: da "mese precedente fisso" a "selezione risorsa + mese"',
          'Pulsante copia sempre visibile (non solo per mese > 1)',
          'Copia pianificazione con skip automatico progetti già presenti (non sovrascrive)',
          'Visualizzazione sempre 12 mesi anche per anno corrente',
          'Drop disabilitato su mesi consolidati (passati)',
          'Click e modifica disabilitati su allocazioni in mesi consolidati'
        ],
        fixed: [
          'Fix colonna database: p.id_hrp invece di p.hrp_id AS id_hrp in SQL gruppi progetti',
          'Fix display id_hrp: spostato da titolo a sezione dettagli (con PM, Cliente, etc.)',
          'Fix ricerca progetti: query numerica ora cerca per id_hrp = N invece di ILIKE %N%',
          'Fix TextField percentuale: rimosso max: 100 per permettere inserimento valori maggiori'
        ]
      },
      {
        version: '1.5.7',
        date: '2025-11-24',
        added: [
          'Sistema Gestione Prompt Parametrizzato: nuova directory backend/config/prompts/ con loader dinamico',
          'Prompt configurabile via .env (SYSTEM_PROMPT_VARIANT): supporto hot-reload senza modifica codice',
          'Prompt "default" v2.1: ottimizzato con ordinamento progetti e senza ragionamento visibile',
          'Prompt "strict" v1.0: ultra-restrittivo che OBBLIGA uso tool e vieta completamente invenzione',
          'Prompt "experimental-short" v1.0: versione concisa per test rapidi',
          'Metriche Performance Chat UI: visualizzazione token/secondo in tempo reale',
          'Box metriche in basso a sinistra chat: ultima query e media generale token/s',
          'Calcolo automatico token approssimato (1 token ≈ 4 caratteri)',
          'Backend calcola token/s e invia via SSE evento "metrics"',
          'Frontend accumula metriche per calcolare media sessione',
          'Tool search_progetti migliorato: supporto ordinamento per budget, data, titolo',
          'Parametri order_by e order_direction per ordinamento dinamico progetti',
          'Query vuota permessa: {"query": ""} ora ritorna tutti i progetti',
          'Whitelist SQL injection per sicurezza ordinamento',
          'README.md per sistema prompts con guida creazione nuove varianti',
          'Documentazione A/B testing tra prompt variants',
          'Metadata prompt: name, version, description, author, createdAt'
        ],
        modified: [
          'Tool search_progetti: parametro query ora opzionale (prima obbligatorio)',
          'Tool search_progetti: ORDER BY dinamico invece di hardcoded "data_inizio DESC"',
          'Prompt default v2.1: rimossi esempi di ragionamento "[Chiama tool...]"',
          'Prompt default v2.1: aggiunte regole esplicite NON MOSTRARE RAGIONAMENTO',
          'Prompt default v2.1: aggiornati esempi uso search_progetti con parametri order_by',
          'ChatPage.tsx: gestione nuovi stati lastQueryMetrics e allMetrics',
          'Backend chat.js: aggiunto conteggio token e calcolo velocità durante streaming',
          'Sistema prompt: da hardcoded in mcpServer.js a loader con getSystemPrompt()'
        ],
        fixed: [
          'Fix search_progetti bloccava query vuote: ora supporta ricerca tutti progetti',
          'Fix ordinamento progetti: prima solo per data, ora configurabile per budget',
          'Fix modello inventa dati: nuovo prompt "strict" con divieti espliciti',
          'Fix visualizzazione metriche chat: posizionamento relativo sopra input',
          'Fix TypeScript: rimosso import CircularProgress inutilizzato',
          'Fix posizionamento metriche: da fixed a absolute con bottom: "100%"'
        ]
      },
      {
        version: '1.5.6',
        date: '2025-11-19',
        added: [
          'Architettura Modulare AI Providers con Strategy + Factory Pattern',
          'Directory backend/src/services/ai-providers/ con BaseProvider, ClaudeProvider, GeminiProvider, ProviderFactory',
          'Logging avanzato provider-specific con prefisso [GEMINI], [CLAUDE]',
          'Diagnostica dettagliata per risposte vuote Gemini (MAX_TOKENS, safety filters)',
          'README.md completo con guida per aggiungere nuovi provider (es. OpenAI)'
        ],
        modified: [
          'AIProviderAdapter refactored: da 304 a 86 linee (-72%)',
          'Rimossi metodi specifici provider e if-else chains',
          'Delegazione completa a provider instances via factory',
          'Isolamento stato provider: cachedGeminiTools spostato in GeminiProvider',
          'Zero logica provider-specific nell\'adapter (puro orchestrator)'
        ],
        fixed: [
          'Fix Gemini MAX_TOKENS Issue: aumentato GOOGLE_MAX_TOKENS da 2048 a 8192',
          'Fix risposte vuote Gemini causate da limite token troppo basso',
          'Fix logging specifico con soluzione suggerita per MAX_TOKENS',
          'Fix distinzione automatica tra MAX_TOKENS (error) e safety filters (warning)'
        ]
      },
      {
        version: '1.5.5',
        date: '2025-11-16',
        added: [
          'Debug Logging searchRisorse: log dettagliati SQL query e parametri per troubleshooting ricerca risorse',
          'Log 🔍 searchRisorse SQL con query completa generata',
          'Log 🔍 searchRisorse params con array parametri PostgreSQL'
        ],
        modified: [
          'System Prompt AI Chat: aggiunto esempio esplicito per query tipo "Quale è la pianificazione di Mario Rossi?"',
          'Pattern corretto: search_risorse → ottieni ID → get_utilizzo_risorsa → presenta dati',
          'Pattern sbagliato evidenziato: indovinare ID, chiedere ID all\'utente, mostrare ragionamento'
        ],
        fixed: [
          'Fix Ricerca Risorse per Nome/Cognome: risolto problema ricerca con nome e cognome separati (es. "Claudia Forzano", "Ivano Turi")',
          'Implementato split automatico query in firstName/lastName quando contiene spazi',
          'Aggiunta condizione SQL con AND tra nome e cognome: (r.nome ILIKE \'%firstName%\' AND r.cognome ILIKE \'%lastName%\')',
          'Supporto ricerca invertita: (r.cognome ILIKE \'%firstName%\' AND r.nome ILIKE \'%lastName%\')',
          'Aggiunto CONCAT per matching su nome completo: CONCAT(r.nome, \' \', r.cognome) ILIKE \'%query%\'',
          'Query singola parola continua a funzionare con OR su nome, cognome, struttura',
          'Fix System Prompt MCP: rimossi backticks da nomi funzioni in sezione DIFFERENZA TRA TOOLS SIMILI',
          'Causa: backticks dentro template string causavano SyntaxError: Unexpected identifier',
          'Fix applicato in mcpServer.js linee 484-534 (searchRisorse) e 1228-1250 (system prompt)'
        ]
      },
      {
        version: '1.5.4',
        date: '2025-11-10',
        added: [
          'Documentazione completa architettura MCP Server: confronto implementazione custom vs best practices MCP standard',
          'Analisi pattern di orchestrazione: limiti agentic loop attuale (max 2 API calls) e soluzioni multi-step',
          'Guida refactoring modulare: da monolite 1296 linee a architettura con tools separati',
          'Confronto transport layers: stdio/HTTP/SSE (MCP standard) vs REST API custom',
          'Pattern di security: implementazione permission checks granulari a livello tool',
          'Validazione input avanzata: Zod schemas + MCP error codes vs error handling custom',
          'Deployment options: Claude Desktop, web clients, microservices architecture'
        ],
        modified: [
          'Analisi scalabilità: da God class a moduli testabili separati per categoria tool',
          'Discovery pattern: da hardcoded tools array a dynamic tool discovery con ListToolsRequestSchema',
          'Error handling: da custom {success: false} a McpError standard codes'
        ],
        fixed: []
      },
      {
        version: '1.5.3',
        date: '2025-11-09',
        added: [
          'AI Assistant (ChatGPT-style): nuova pagina dedicata per interagire con l\'assistente AI',
          'Integrazione MCP Server con Claude Haiku per query intelligenti su database',
          'Tool MCP per risorse, progetti, allocazioni, budget, economics e statistiche',
          'Sistema di logging strutturato per MCP Server con rotazione giornaliera (7 giorni retention)',
          'Log completi LLM: registrazione prompt utente, system prompt, risposte Claude e tool calls',
          'Menu AI Assistant nella sezione Analytics con icona chat',
          'Interfaccia chat con header, area messaggi scrollabile e input fisso',
          'Gestione sessioni chat con memoria conversazionale',
          'Supporto markdown e formatting nei messaggi'
        ],
        modified: [
          'Chat UI: spostata da sidebar a pagina dedicata con layout più ampio',
          'MCP System Prompt: regole più stringenti per evitare allucinazioni (DEVE usare tools, MAI inventare dati)',
          'Tool get_allocazioni: aggiunto parametro risorsa_nome per ricerca per nome/cognome oltre a ID',
          'Ricerca allocazioni: supporto ILIKE case-insensitive su nome, cognome e nome completo',
          'Sidebar: rimosso pannello chat collassabile in favore del link dedicato'
        ],
        fixed: [
          'Fix SQL MCP Server: corrette 4 query con colonne inesistenti (stato, codice)',
          'Fix colonna stato in getProgettiStatistiche: usata logica data_fine invece di campo stato',
          'Fix colonna codice: sostituita con tipologia in searchProgetti, getAllocazioni, getEconomicsProgetto',
          'Fix ricerca allocazioni per nome risorsa: ora trova correttamente allocazioni anche quando l\'utente fornisce nome invece di ID',
          'Fix LLM hallucinations: system prompt migliorato impedisce invenzione di nomi/dati'
        ]
      },
      {
        version: '1.5.2',
        date: '2025-11-09',
        added: [
          'Dashboard Dirigenziale: aggiunto filtro Struttura prima del filtro Risorse',
          'Filtro Struttura con funzionalità Seleziona tutti/Deseleziona tutti',
          'Default tutte le strutture selezionate al caricamento pagina'
        ],
        modified: [
          'Dashboard Dirigenziale: filtro mesi ora seleziona tutti i mesi dell\'anno corrente di default (prima solo da mese corrente in poi)',
          'Filtro Risorse ora viene automaticamente aggiornato in base alle strutture selezionate'
        ],
        fixed: []
      },
      {
        version: '1.5.1',
        date: '2025-11-08',
        added: [
          'Script debug verify_durata_fields.js per analisi campi durata in HRP'
        ],
        modified: [],
        fixed: [
          'Fix Calcolo Rendicontazioni: usato campo durata invece di durata_giornaliera (durata = durata_giornaliera × numero_giorni)',
          'Fix over-allocation errata per attività multi-giorno (es. consolidamenti)',
          'Fix Aggiornamento Timestamp Last Sync: calcolo newSyncTimestamp spostato prima del loop per garantire sync incrementale',
          'Fix timestamp aggiornato correttamente anche quando allocazioni falliscono',
          'Fix Errore "Cannot read properties of undefined (reading inserted)": gestione corretta allocazioni manuali esistenti',
          'Fix controllo upsertResult.rows.length > 0 prima di accedere a rows[0].inserted',
          'Fix warning esplicito per allocazioni manuali non sovrascritte durante sync'
        ]
      },
      {
        version: '1.5.0',
        date: '2025-11-08',
        added: [
          'Sistema di Sincronizzazione Rendicontazioni da HRP: conversione automatica rendicontazioni in allocazioni mensili',
          'Nuova pagina "Sync Rendicontazioni" nella sezione Admin',
          'Sincronizzazione incrementale: elabora solo rendicontazioni con data_inizio dopo ultimo sync',
          'Sincronizzazione solo per il passato: escluse automaticamente rendicontazioni da oggi in poi (data_inizio < CURDATE())',
          'Calcolo percentuale allocazione: 1 mese = 20gg × 8h × 60min = 9600min',
          'Protezione allocazioni manuali: solo allocazioni con sorgente=\'H\' vengono aggiornate, quelle sorgente=\'M\' mai sovrascritte',
          'Over-allocation tracking: percentuali >100% salvate e segnalate con warning',
          'Preview rendicontazioni: anteprima fino a 100 allocazioni da sincronizzare con dettagli risorsa/progetto',
          'Warning progetti non trovati: segnalazione progetti con rendicontazioni ma assenti in MyPlanner',
          'Statistiche sincronizzazione: numero allocazioni da HRP, risorse coinvolte, timestamp ultimo sync',
          'Test connessione HRP: verifica connettività database MariaDB prima della sync',
          'API Endpoints: test-connection, stats, last-sync, preview, sync-all',
          'Frontend con 4 funzionalità: Test Connessione, Statistiche, Preview, Sync All',
          'Colonna sorgente in tabella allocazioni: distingue allocazioni Manuali (M), HRP (H), ERP (E)',
          'Rimozione constraint percentuale <=100: consente tracking over-allocation',
          'Tabella sync_config: memorizza timestamp ultimo sync rendicontazioni (default: 1980-01-01)',
          'Mapping automatico IDs: utente_id (HRP) → risorse.id_hrp, progetto_id (HRP) → progetti.id_hrp',
          'Upsert intelligente: INSERT con ON CONFLICT per aggiornare solo allocazioni HRP esistenti',
          'Fix calcolo durata: utilizzo campo durata_giornaliera invece di TIMESTAMPDIFF per evitare errori su attività multi-giorno',
          'Database migrations: 010_add_sorgente_to_allocazioni.sql, 011_remove_percentuale_constraint.sql, 012_init_sync_rendicontazioni_config.sql',
          'Documentazione completa: backend/docs/SYNC_RENDICONTAZIONI.md con architettura, API, troubleshooting'
        ],
        modified: [
          'Sync Progetti: rimossi filtri flag_ordine e stato_chiuso_id per sincronizzare TUTTI i progetti da HRP',
          'Query sync progetti: mantiene solo filtro temporale su last_update, rimossi filtri di stato',
          'PlanningContext: aumentato limite progetti da 1.000 a 50.000 per gestire tutti i progetti sincronizzati',
          'Constraint allocazioni.percentuale: da CHECK (percentuale >= 0 AND percentuale <= 100) a CHECK (percentuale >= 0)',
          'Query rendicontazioni: utilizzo SUM(durata_giornaliera) invece di TIMESTAMPDIFF per calcolo corretto minuti lavorati'
        ],
        fixed: [
          'Fix calcolo over-allocation: utente 149 luglio 2024 da 590% errato a 63% corretto usando durata_giornaliera',
          'Fix titoli progetti "N/A": aumentato limite caricamento da 1.000 a 50.000 progetti in PlanningContext',
          'Fix progetti mancanti: rimossi filtri stato progetti per includere tutti i progetti con rendicontazioni'
        ]
      },
      {
        version: '1.4.3.1',
        date: '2025-11-08',
        added: [],
        modified: [],
        fixed: [
          'Fix URL Produzione Sync Progetti: risolto problema di chiamate API hardcoded a localhost:3001 in ambiente Kubernetes',
          'Refactoring completo di SyncProgettiPage.tsx per usare servizio API centralizzato',
          'Aggiunte 6 funzioni sync-progetti in services/api.ts (testSyncConnection, getSyncStats, getLastSync, syncSingleProject, syncPreview, syncAllProjects)',
          'Rimosse chiamate axios dirette con URL hardcoded',
          'Rimozione gestione manuale header x-user-id (ora gestito dall\'interceptor axios)',
          'Tutti gli endpoint sync ora rispettano configurazione BASE_URL (/myplanner/api in produzione)'
        ]
      },
      {
        version: '1.4.3',
        date: '2025-11-08',
        added: [
          'Sistema di Sincronizzazione Progetti da HRP: integrazione completa con database esterno MariaDB',
          'Nuova pagina "Sync Progetti" nella sezione Admin del menu laterale',
          'Sincronizzazione Incrementale Intelligente: usa timestamp last_update da HRP come marker',
          'Tabella sync_config per memorizzare ultimo timestamp di sincronizzazione',
          'Prima sincronizzazione recupera tutti i progetti (timestamp default: 1980-01-01)',
          'Sincronizzazioni successive processano solo progetti modificati dopo ultimo sync',
          'Test connessione database HRP (MariaDB su 192.168.9.28:3306)',
          'Sincronizzazione singolo progetto per ID HRP',
          'Sincronizzazione massiva con preview (max 100 progetti)',
          'Mapping automatico campi HRP → MyPlanner con JOIN per email commerciale/PM',
          'Mapping Budget Corretto: Number(gg_previste_delivery) + Number(gg_previste_postvendita) → budget_gg_uu',
          'Filtri automatici: flag_ordine = 1 e stato_chiuso_id NOT IN (1,2,3)',
          'Indicatori CREATE/UPDATE per ogni progetto in preview',
          'Statistiche: progetti sincronizzati, ultima modifica, timestamp ultimo sync',
          'Paginazione Server-Side per Gestione Progetti: tabella progetti in Admin → Progetti con paginazione (25/50/100 righe)',
          'Ricerca full-text su titolo, descrizione, erpnext_key, PM, commerciale, cliente, cliente_finale',
          'Debounced search (500ms) per ridurre carico server',
          'Loading spinner durante caricamento dati'
        ],
        modified: [
          'Menu Struttura: voci Anagrafiche (Progetti, Risorse, Figure Professionali, Skills, Strutture, Utenti, Permessi) spostate sotto sezione Admin',
          'Lista Progetti Pianificazione: implementato Infinite Scroll con caricamento on-demand (batch da 50 progetti)',
          'Ricerca server-side con debouncing (500ms) su tutti i dati in database (non solo progetti caricati)',
          'Intersection Observer API nativa per caricamento automatico senza librerie esterne',
          'Performance ottimizzata: carica solo i progetti necessari, non più tutti insieme',
          'Placeholder aggiornato: "Cerca progetti su tutti i dati..."',
          'Contatore "N progetti trovati" visibile sotto casella ricerca',
          'Indicatore "Fine della lista (N progetti)" quando tutti i dati sono stati caricati',
          'Dependencies: aggiunta libreria mysql2 per connessione MariaDB/MySQL'
        ],
        fixed: []
      },
      {
        version: '1.4.2',
        date: '2025-11-06',
        added: [
          'Campi ID esterni per risorse (id_hrp e id_erp) per integrazione con sistemi HR/Payroll e ERP',
          'Colonne ID HRP e ID ERP in tabella risorse',
          'Form risorsa con campi ID HRP e ID ERP con helper text esplicativi',
          'Indici database per lookup veloci su ID esterni'
        ],
        modified: [
          'CRUD Risorse aggiornato con gestione ID esterni',
          'Backend routes POST/PUT /api/risorse gestiscono id_hrp e id_erp',
          'TypeScript interfaces Risorsa e RisorsaInput con nuovi campi'
        ],
        fixed: []
      },
      {
        version: '1.4.1',
        date: '2025-11-06',
        added: [
          'Componente BudgetProgressBarMini: visualizzazione compatta inline dello stato budget',
          'API Consolidamento Budget (/api/consolidamento) per gestione rendicontazioni timesheet',
          'Colonna consolidato (BOOLEAN) in tabella allocazioni per distinguere previsionali da consolidati',
          'Endpoint GET /consolidamento/progetti/:id per stato consolidamento progetto'
        ],
        modified: [
          'Semplificazione modello budget: rimossa colonna budget_consuntivato_gg_uu',
          'Campo budget_pianificato_gg_uu ora include sia previsionali che consolidati',
          'Formula budget disponibile semplificata: budget_gg_uu - budget_pianificato_gg_uu',
          'Layout tabella Economics con colonna Stato Budget inline (rimossa riga separata)',
          'BudgetCalculator utility aggiornato per nuova logica consolidamento'
        ],
        fixed: [
          'Fix interfacce TypeScript per rimuovere budget_consuntivato_gg_uu',
          'Fix props componente BudgetProgressBarMini',
          'Fix layout Economics page con nuova struttura colonne'
        ]
      },      
      {
        version: '1.4.0',
        date: '2025-11-03',
        added: [
          'Sistema di Monitoraggio Budget Progetti con tracking budget pianificato vs disponibile',
          'Colonne budget_pianificato_gg_uu e budget_consuntivato_gg_uu per tracciamento separato',
          'Utility module budgetCalculator.js con funzioni centralizzate per calcoli',
          'Calcolo automatico budget pianificato: (percentuale / 100) * 22 giorni lavorativi',
          'Ricalcolo real-time del budget ad ogni modifica allocazione',
          'Componente BudgetProgressBar con barra colorata (verde >20%, giallo 0-20%, rosso <0%)',
          'Componente BudgetProgressBarCompact per visualizzazione nei box allocazione',
          'Auto-refresh dati budget dopo modifica percentuale allocazione',
          'Script SQL batch per ricalcolo iniziale budget su progetti esistenti'
        ],
        modified: [
          'Backend Routes Allocazioni con ricalcolo budget in 4 endpoint (POST/PUT/DELETE)',
          'TypeScript interfaces aggiornate con campi budget pianificato e consuntivato',
          'ProjectList con visualizzazione BudgetProgressBar per progetti con budget > 0',
          'MonthGrid con BudgetProgressBarCompact nei box allocazione mensili'
        ],
        fixed: [
          'Fix transaction safety: ricalcolaBudgetPianificato() accetta client per evitare conflitti',
          'Fix eventi bloccati in MonthGrid: risolto silent transaction failures',
          'Fix TypeScript: rimossi import inutilizzati (Chip, Alert)',
          'Fix pointer events: aggiunto pointerEvents none a BudgetProgressBarCompact'
        ]
      },
      {
        version: '1.3.1',
        date: '2025-11-02',
        added: [
          'Sistema Foto Profilo Risorse con upload e preview',
          'Componente AvatarUpload con ridimensionamento automatico a 200x200px',
          'Compressione JPEG al 80% per ottimizzare dimensioni',
          'Validazione formato immagine e dimensione massima (5MB)',
          'Visualizzazione avatar in ResourceList (40x40px accanto al nome)',
          'Colonna "Foto" in RisorsePage con avatar',
          'Placeholder icon quando non presente foto profilo',
          'Storage Base64 in database (nessun file system)',
          'Backend routes aggiornate per gestione campo foto'
        ],
        modified: [],
        fixed: []
      },
      {
        version: '1.3.0',
        date: '2025-11-02',
        added: [
          'Sistema di Gestione Skills con tabelle skills e risorse_skills',
          '17 skills predefinite raggruppate per categoria (Backend, Frontend, Database, IoT, Mobile, Architecture, Management)',
          'Livelli di competenza configurabili (Base, Intermedio, Avanzato, Expert)',
          'Pagina Skills per gestione anagrafica con filtri per categoria',
          'Tab Skills in Risorsa per gestione skills dalla risorsa stessa',
          'Visualizzazione skills in ResourceList e RisorsePage con chip colorati',
          'Menu Sidebar con nuova voce "Skills" in Anagrafiche',
          'API REST complete per CRUD skills e associazioni risorsa-skill'
        ],
        modified: [
          'ResourceForm con interfaccia a tab (Informazioni Base + Skills)',
          'Backend GET /api/risorse include array skills con JSON aggregation',
          'TypeScript interfaces aggiornate per campo skills array',
          'Ricerca risorse estesa per includere skills'
        ],
        fixed: [
          'Rimosso campo testuale skill dalla tabella risorse',
          'Aggiornati filtri di ricerca per usare array skills strutturato'
        ]
      },
      {
        version: '1.2.2',
        date: '2025-11-02',
        added: [
          'Script PowerShell backup.ps1 per backup database produzione',
          'Script PowerShell restore.ps1 per restore database su sviluppo',
          'Documentazione completa backup/restore in README_BACKUP.md',
          'Gestione automatica schema public (esclusione pgbouncer)',
          'Applicazione automatica vincoli UNIQUE durante restore'
        ],
        modified: [],
        fixed: [
          'Fix null-safety in EconomicsPage per valori budget nulli',
          'Fix precisione decimale budget residuo (arrotondamento a 1 cifra)',
          'Fix dimensione label grafico "Distribuzione Risorse per Struttura"',
          'Fix encoding UTF-8 BOM in script PowerShell restore'
        ]
      },
      {
        version: '1.2.1',
        date: '2025-11-01',
        added: [],
        modified: [],
        fixed: [
          'Fix formato data rilascio versione (corretto da 11/01/2025 a 01/11/2025)'
        ]
      },
      {
        version: '1.2.0',
        date: '2025-11-01',
        added: [
          'Pagina Capacity Planning con analisi FTE richiesti vs disponibili',
          'Calcolo FTE richiesti basato su budget residuo e durata progetto',
          'Calcolo FTE disponibili da allocazioni pianificate',
          'Indicatore Gap FTE per identificare fabbisogno assunzioni',
          'Campi data_inizio e data_fine nell\'anagrafica progetti',
          'Visualizzazione date nei progetti con formato italiano',
          'Tabella FTE disponibili per mese con indicatori colorati',
          'Tabella dettaglio FTE richiesti per progetto',
          'Card riepilogo con 4 indicatori: FTE Richiesti, Disponibili, Gap e Periodo',
          'Calcolo dinamico periodo analisi (mesi rimanenti anno corrente)'
        ],
        modified: [
          'Menu Reportistica con nuova voce "Capacity Planning"',
          'Logica calcolo FTE basata su durata reale progetto (data_fine - data_inizio)',
          'Backend progetti per supportare campi timestamp data_inizio e data_fine'
        ],
        fixed: [
          'Fix autorizzazione modifica utenti (mancavano header autenticazione)',
          'Fix autorizzazione eliminazione utenti (mancavano header autenticazione)',
          'Fix calcolo durata progetti (ora usa date effettive progetto, non anno corrente)'
        ]
      },
      {
        version: '1.1.0',
        date: '2025-11-01',
        added: [
          'Pagina Economics con analisi economica completa',
          'Riepilogo generale con 6 indicatori chiave (budget, pianificato, da pianificare)',
          'Tabella riepilogo per progetto con drilldown per risorsa/mese',
          'Filtri avanzati per risorse, mesi e progetti',
          'Calcolo automatico costi basato su allocazioni e costo orario',
          'Pulsante ricalcolo manuale budget residui',
          'Campo costo_orario nella tabella risorse',
          'Tipo DOUBLE PRECISION per campi budget',
          'Aggiornamento automatico budget residuo ad ogni modifica allocazioni'
        ],
        modified: [
          'Dashboard con indicatori di allocazione media',
          'Miglioramenti UI con card gradient per indicatori economici',
          'Ottimizzazione query SQL per calcoli economici'
        ],
        fixed: [
          'Fix calcolo giorni uomo per supportare valori decimali',
          'Fix aggiornamento budget residuo in tempo reale'
        ]
      },
      {
        version: '1.0.0',
        date: '2025-10-28',
        added: [
          'Sistema di Pianificazione completo con drag & drop',
          'Dashboard con panoramica progetti e risorse',
          'Gestione Anagrafiche (Progetti, Risorse, Strutture)',
          'Sistema di Autenticazione con ruoli e permessi',
          'Export Pianificazione in Excel',
          'Database PostgreSQL con schema completo'
        ],
        modified: [],
        fixed: []
      }
    ];

    setChangelog(changelogData);
  }, []);

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'added':
        return '#4caf50';
      case 'modified':
        return '#2196f3';
      case 'fixed':
        return '#ff9800';
      default:
        return '#9e9e9e';
    }
  };

  const getCategoryLabel = (category: string) => {
    switch (category) {
      case 'added':
        return 'Aggiunto';
      case 'modified':
        return 'Modificato';
      case 'fixed':
        return 'Corretto';
      default:
        return category;
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box component="img" src="/logo-myplanner.png" alt="HarpaPlanner" sx={{ width: 56, height: 56 }} />
        <Box>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            HarpaPlanner
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Dettagli versione e changelog completo
          </Typography>
        </Box>
      </Box>

      {/* Card Informazioni Versione */}
      <Box sx={{ mb: 4, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <Card sx={{ flex: '1 1 calc(33.333% - 16px)', minWidth: 250, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <InfoIcon sx={{ color: 'white', mr: 1 }} />
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                Applicazione
              </Typography>
            </Box>
            <Typography variant="h5" fontWeight="bold" sx={{ color: 'white' }}>
              {versionData.name}
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', mt: 1 }}>
              {versionData.description}
            </Typography>
          </CardContent>
        </Card>

        <Card sx={{ flex: '1 1 calc(33.333% - 16px)', minWidth: 250, background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <TagIcon sx={{ color: 'white', mr: 1 }} />
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                Versione Corrente
              </Typography>
            </Box>
            <Typography variant="h5" fontWeight="bold" sx={{ color: 'white' }}>
              v{versionData.version}
            </Typography>
          </CardContent>
        </Card>

        <Card sx={{ flex: '1 1 calc(33.333% - 16px)', minWidth: 250, background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <CalendarIcon sx={{ color: 'white', mr: 1 }} />
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                Data Rilascio
              </Typography>
            </Box>
            <Typography variant="h5" fontWeight="bold" sx={{ color: 'white' }}>
              {new Date(versionData.releaseDate).toLocaleDateString('it-IT', {
                day: '2-digit',
                month: 'long',
                year: 'numeric'
              })}
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Changelog */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h5" fontWeight="bold" gutterBottom>
          Changelog
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Cronologia completa delle modifiche e miglioramenti
        </Typography>

        {changelog.map((release, index) => (
          <Box key={release.version} sx={{ mb: 4 }}>
            {/* Release Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Chip
                label={`v${release.version}`}
                sx={{
                  fontWeight: 'bold',
                  fontSize: '1rem',
                  height: 36,
                  backgroundColor: index === 0 ? '#667eea' : '#e0e0e0',
                  color: index === 0 ? 'white' : 'inherit'
                }}
              />
              <Typography variant="body1" color="text.secondary">
                {new Date(release.date).toLocaleDateString('it-IT', {
                  day: '2-digit',
                  month: 'long',
                  year: 'numeric'
                })}
              </Typography>
            </Box>

            {/* Sezione Aggiunto */}
            {release.added.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      backgroundColor: getCategoryColor('added'),
                      mr: 1
                    }}
                  />
                  <Typography variant="subtitle2" fontWeight="bold" sx={{ color: getCategoryColor('added') }}>
                    {getCategoryLabel('added')}
                  </Typography>
                </Box>
                <Box sx={{ pl: 2 }}>
                  {release.added.map((item, i) => (
                    <Typography key={i} variant="body2" sx={{ mb: 0.5 }}>
                      • {item}
                    </Typography>
                  ))}
                </Box>
              </Box>
            )}

            {/* Sezione Modificato */}
            {release.modified.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      backgroundColor: getCategoryColor('modified'),
                      mr: 1
                    }}
                  />
                  <Typography variant="subtitle2" fontWeight="bold" sx={{ color: getCategoryColor('modified') }}>
                    {getCategoryLabel('modified')}
                  </Typography>
                </Box>
                <Box sx={{ pl: 2 }}>
                  {release.modified.map((item, i) => (
                    <Typography key={i} variant="body2" sx={{ mb: 0.5 }}>
                      • {item}
                    </Typography>
                  ))}
                </Box>
              </Box>
            )}

            {/* Sezione Corretto */}
            {release.fixed.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      backgroundColor: getCategoryColor('fixed'),
                      mr: 1
                    }}
                  />
                  <Typography variant="subtitle2" fontWeight="bold" sx={{ color: getCategoryColor('fixed') }}>
                    {getCategoryLabel('fixed')}
                  </Typography>
                </Box>
                <Box sx={{ pl: 2 }}>
                  {release.fixed.map((item, i) => (
                    <Typography key={i} variant="body2" sx={{ mb: 0.5 }}>
                      • {item}
                    </Typography>
                  ))}
                </Box>
              </Box>
            )}

            {index < changelog.length - 1 && <Divider sx={{ mt: 3 }} />}
          </Box>
        ))}
      </Paper>

      {/* Footer Info */}
      <Box sx={{ mt: 3, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
        <Typography variant="body2" color="text.secondary" align="center">
          Tecnologie: React 18 + TypeScript + Material-UI • Node.js + Express • PostgreSQL
        </Typography>
      </Box>
    </Box>
  );
};

export default AboutPage;
